import type { OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import { Router } from '@angular/router';
import type { PaginationInstance } from 'ngx-pagination';
import type { MegaErrors } from '../../../mega-exchange/services/interfaces/mega-error';
import { FmLoadingPageService } from '../../../shared/services/fm-loading-page.service';
import type { CommitExchangeResponse } from '../../services/interfaces/commit-exchange-response';
import type { SettledExchangesResponse } from '../../services/interfaces/SettledExchangesResponse';
import { PassdataService } from '../../services/passdata.service';
import { RecombinationsService } from '../../services/recombinations.service';

@Component({
  standalone: false,
  selector: 'invd-settled-exchanges-listings',
  templateUrl: './settled-exchanges-listings.component.html',
  styleUrls: ['./settled-exchanges-listings.component.scss'],
})
export class SettledExchangesListingsComponent implements OnInit {
  @Input() messageTitle = 'Status';
  @Input() messageBody = '';

  public displayPagination = false;
  public presentSortedColumn = '';
  public presentSortedColumnIsAscending = false;
  public settledExchangeResponseError = false;
  public statusCode: number;
  public isConfirmationLinkClicked = true;
  public settledExchangesData: any = new Object();
  public config: PaginationInstance = {
    id: 'settled-exchanges-container',
    itemsPerPage: 50,
    currentPage: 1,
  };

  private router = inject(Router);
  private rcrService = inject(RecombinationsService);
  private passDataService = inject(PassdataService);
  private fmLoadingPageService = inject(FmLoadingPageService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.isConfirmationLinkClicked = true;
    this.getSettledExchangesData();
  }

  private getSettledExchangesData() {
    this.rcrService.getSettledExchanges().subscribe({
      next: (data) => this.handleSettledExchangesResponse(data as SettledExchangesResponse),
      error: (error) => this.handleSettledExchangeResponseError(error),
    });
  }

  private retrieveExchangeServiceCall(request: any) {
    this.rcrService.retrieveExchangeService(request).subscribe({
      next: (data) => this.handleRetrieveExchangeResponse(data as CommitExchangeResponse),
      error: (error) => this.handleRetrieveExchangeResponseError(error),
    });
  }

  private handleRetrieveExchangeResponse(commitExchangeResp: CommitExchangeResponse) {
    this.passDataService.setCommitExchangeResponse(commitExchangeResp);
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.isConfirmationLinkClicked = true;
    this.cdr.markForCheck();
    this.router.navigate([
      'rcr-exchange/settled-exchanges',
      commitExchangeResp.exchangeConfirmationNumber,
      'confirm',
    ]);
  }

  private handleRetrieveExchangeResponseError(_error: any) {
    this.isConfirmationLinkClicked = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public retrieveExchangeData(index: number): void {
    const retrieveExchangeRequestData: any = new Object();
    /**
     * #TODO TRADER ID
     * when conditional setup when BE gives us a trader id
     *
     * @type {any}
     */
    //retrieveExchangeRequestData['sttsTraderId'] = null;
    const exchangeConfirmatoinNum: number =
      this.settledExchangesData.settledExchanges[index].exchangeConfirmationNumber;
    retrieveExchangeRequestData.exchangeConfirmationNumber = exchangeConfirmatoinNum;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    if (this.isConfirmationLinkClicked) {
      this.retrieveExchangeServiceCall(retrieveExchangeRequestData);
    }

    this.isConfirmationLinkClicked = false;
  }

  public sortByGridByColumnName(columnName) {
    if (this.settledExchangesData.hasOwnProperty('settledExchanges')) {
      if (this.presentSortedColumn !== columnName) {
        this.presentSortedColumnIsAscending = true;
      } else {
        this.presentSortedColumnIsAscending = !this.presentSortedColumnIsAscending;
      }
      const isAscending = this.presentSortedColumnIsAscending ? 1 : -1;

      this.settledExchangesData.settledExchanges.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
      });
      this.presentSortedColumn = columnName;
    }
  }

  private handleSettledExchangesResponse(settledExchangesObject: SettledExchangesResponse) {
    this.settledExchangesData = settledExchangesObject;
    this.settledExchangeResponseError = false;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    if (settledExchangesObject.settledExchanges.length > 50) {
      this.displayPagination = true;
    } else {
      this.displayPagination = false;
      this.config = {
        id: 'settled-exchanges-container',
        itemsPerPage: 50,
        currentPage: 1,
      };
    }
    this.cdr.markForCheck();
  }

  private handleSettledExchangeResponseError(error: any) {
    const errorObject: MegaErrors = error.error;
    this.settledExchangeResponseError = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.displayPagination = false;
    this.config = {
      id: 'settled-exchanges-container',
      itemsPerPage: 50,
      currentPage: 1,
    };
    this.messageBody = errorObject.messages[0].errorMessage;
    this.cdr.markForCheck();
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
